import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import PrescriptionTabs from '../../clinical-services/service-steps/education/prescription-tabs';
import {
  InterventionDetails,
  TargetsResults,
  PharmacistFollowUp,
  PrescriberFollowUp,
  Notification,
  Documentation,
  Billing
} from './steps';
import { getInterventionById, fetchClinicalServiceForIntervention, resetClinicalServiceForIntervention } from '../../../../redux/modules/intervention/actions';
import { defaultInterventionSteps } from '../../text/data';
import { formatDateToDD_MMM_YYYY } from '../../../../helpers/functions';
import { getInterventionStatusColor, isActiveInterventionStatus, isActiveOrWaitingInterventionStatus } from './data';

const InterventionRecord = ({ interventionId }) => {
  // Get data from Redux intervention state
  const { interventions, currentIntervention, fetchByIdFailedForId, loading: interventionsLoading } = useSelector((state) => state.intervention);
  const dispatch = useDispatch();
  const { id: patientId } = useParams();

  // Memoize resolved row so we do not re-derive from unstable references on every render
  const intervention = useMemo(() => {
    if (!interventionId) return null;
    if (String(currentIntervention?.id) === String(interventionId)) return currentIntervention;
    return interventions?.find((it) => String(it.id) === String(interventionId)) ?? null;
  }, [interventionId, currentIntervention, interventions]);

  // If fetch failed for this intervention, avoid rendering stale step content (no early return before hooks).
  const shouldNotRenderSteps =
    Boolean(fetchByIdFailedForId) &&
    Boolean(interventionId) &&
    fetchByIdFailedForId === interventionId;

  const interventionList = Array.isArray(interventions) ? interventions : [];
  const [selectedInterventionFilter, setSelectedInterventionFilter] = useState("all");

  useEffect(() => {
    const handleFilterChanged = (e) => {
      const nextFilter = e?.detail?.selectedFilter;
      if (!nextFilter) return;
      setSelectedInterventionFilter(nextFilter);
    };

    window.addEventListener("intervention-filter-changed", handleFilterChanged);
    return () =>
      window.removeEventListener(
        "intervention-filter-changed",
        handleFilterChanged
      );
  }, []);

  // Empty state only when no Active / Waiting items in the fetched list (sidebar source of truth)
  const hasActiveOrWaitingInList = interventionList.some((item) =>
    isActiveOrWaitingInterventionStatus(item?.status)
  );
  const hasActiveInList = interventionList.some((item) =>
    isActiveInterventionStatus(item?.status)
  );

  const shouldShowNoActiveInterventions =
    (
      (!interventionId && !hasActiveOrWaitingInList) ||
      (selectedInterventionFilter === "active" && !hasActiveInList)
    ) &&
    (!interventionsLoading || interventionList.length > 0);
  
  // Tab state
  const [activeTab, setActiveTab] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [currentInterventionSteps, setCurrentInterventionSteps] = useState([]);
  const lastSyncedInterventionIdRef = useRef(null);
  const lastSyncedSavedStepRef = useRef(null);
  const lastSyncedStepsKeyRef = useRef("");

  // Load selected intervention detail for any status (Active/Completed/Cancelled)
  useEffect(() => {
    if (interventionId && patientId) {
      dispatch(getInterventionById(patientId, interventionId));
    }
  }, [interventionId, patientId, dispatch]);


  // Linked AMR/minor-ailment fetch for the selected intervention when service data exists.
  const clinicalServiceType = intervention?.clinicalServiceType;
  const clinicalServiceId = intervention?.clinicalServiceId;
  const resolvedInterventionId = intervention?.id;

  useEffect(() => {
    if (!interventionId || String(resolvedInterventionId) !== String(interventionId)) {
      dispatch(resetClinicalServiceForIntervention());
      return;
    }

    if (clinicalServiceType && clinicalServiceId?.trim()) {
      dispatch(fetchClinicalServiceForIntervention(clinicalServiceType, clinicalServiceId));
    } else {
      dispatch(resetClinicalServiceForIntervention());
    }
  }, [interventionId, resolvedInterventionId, clinicalServiceType, clinicalServiceId, dispatch]);
  
  // Load steps from reducer (persisted), fallback to defaults if missing
  useEffect(() => {
    if (intervention?.interventionSteps && intervention.interventionSteps.length > 0) {
      setCurrentInterventionSteps(intervention.interventionSteps);
    } else if (intervention) {
      setCurrentInterventionSteps(defaultInterventionSteps);
    } else {
      setCurrentInterventionSteps([]);
    }
  }, [intervention?.interventionSteps, intervention]);

  const savedCurrentStep =
    intervention?.interventionAssessment?.interventionAssessmentForm
      ?.currentStep || 1;
  const currentInterventionStepsKey = useMemo(
    () =>
      currentInterventionSteps
        .map(
          (step) =>
            `${step.name}:${step.disabled === true}:${step.comingSoon === true}`
        )
        .join("|"),
    [currentInterventionSteps]
  );

  // Sync the selected tab from persisted data only when the selected record,
  // persisted step, or step availability changes.
  useEffect(() => {
    if (!intervention || currentInterventionSteps.length === 0) return;

    const interventionChanged =
      intervention.id !== lastSyncedInterventionIdRef.current;
    const savedStepChanged =
      savedCurrentStep !== lastSyncedSavedStepRef.current;
    const stepsChanged =
      currentInterventionStepsKey !== lastSyncedStepsKeyRef.current;

    if (!interventionChanged && !savedStepChanged && !stepsChanged) return;

    lastSyncedInterventionIdRef.current = intervention.id;
    lastSyncedSavedStepRef.current = savedCurrentStep;
    lastSyncedStepsKeyRef.current = currentInterventionStepsKey;
    setCurrentStep(savedCurrentStep);

    const stepIndex = Math.max(
      0,
      Math.min(savedCurrentStep - 1, currentInterventionSteps.length - 1)
    );
    const targetTab = currentInterventionSteps[stepIndex];

    if (targetTab && !targetTab.disabled && !targetTab.comingSoon) {
      setActiveTab(targetTab);
    } else {
      const firstEnabledTab = currentInterventionSteps.find(
        (tab) => !tab.disabled && !tab.comingSoon
      );
      if (firstEnabledTab) {
        setActiveTab(firstEnabledTab);
        setCurrentStep(currentInterventionSteps.indexOf(firstEnabledTab) + 1);
      }
    }
  }, [intervention, savedCurrentStep, currentInterventionSteps, currentInterventionStepsKey]);

  // Same empty UI for fetch-by-id failure or no Active/Waiting in list; conditions are separate above, markup shared once.
  if (shouldNotRenderSteps || shouldShowNoActiveInterventions) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-white">
        <div className="text-center min-h-[250px]">
          <div className="text-REELTeal text-[30px] font-medium mb-4 border border-teal-100 rounded-md px-4 py-4 shadow-sm ">
            No Active Interventions
          </div>
        </div>
      </div>
    );
  }

  // Selection in URL or list implies a row, but detail/list not resolved yet — no fallback text (avoids flicker)
  if (!intervention) {
    return null;
  }

  // Handle tab click
  const handleTabClick = (tab) => {
    if (!tab.disabled && !tab.comingSoon) {
      const tabIndex = currentInterventionSteps.findIndex(t => t.name === tab.name);
      if (tabIndex < 0) return;
      const newStep = tabIndex + 1;
      
      setCurrentStep(newStep);
      setActiveTab(tab);
    }
  };

  // Render content based on active tab
  const renderTabContent = () => {
    if (!activeTab) return null;

    const tabContentProps = {
      intervention,
      currentInterventionSteps,
      currentStep,
    };

    const tabComponents = {
      "Intervention Details": <InterventionDetails {...tabContentProps} />,
      "Targets & Results": <TargetsResults {...tabContentProps} />,
      "Pharmacist Follow Up": <PharmacistFollowUp {...tabContentProps} />,
      "Prescriber Follow Up": <PrescriberFollowUp {...tabContentProps} />,
      "Notification": <Notification {...tabContentProps} />,
      "Documentation": <Documentation {...tabContentProps} />,
      "Billing": <Billing {...tabContentProps} />
    };

    return tabComponents[activeTab.name] || null;
  };

  const getInterventionTypeDisplay = (type) => {
    switch (type) {
      case 'PharmaceuticalOpinion':
        return 'Pharmaceutical Opinion';
      case 'prescriptionAdaptation':
        return 'Prescription Adaptation';
      default:
        return type || 'Intervention';
    }
  };

  return (
    <div className="w-full h-full flex flex-col bg-white">
      {/* Tabs */}
      <div className="bg-white">
        <PrescriptionTabs
          activeTab={activeTab}
          setActiveTab={handleTabClick}
          tabs={currentInterventionSteps}
        />
      </div>

      {/* Header */}
      <div
        className={`w-full px-4 py-3 mt-3 border-2 rounded ${getInterventionStatusColor(intervention?.status)}`}
      >
        <div className="flex items-center justify-between">
          <div className="text-gray-800 font-semibold text-[20px]">
            {`${getInterventionTypeDisplay(intervention?.interventionType) || ''}`}
            {intervention?.clinicalServiceType ? ` - ${intervention.clinicalServiceType}` : ''}
          </div>
          <div className="text-gray-600 text-sm">Intervention Initiated On:&nbsp;&nbsp;
            {intervention?.createdDate
              ? formatDateToDD_MMM_YYYY(new Date(intervention.createdDate))
              : ''}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-auto">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default InterventionRecord;
